import BaseListPresenter from "../../base/BaseListPresenter";

class MyCalendarPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;

    this.isMounted = false;
  }

  componentDidMount() {
    this.isMounted = true;
    this.init();
    this.getLocations();
    return this.getObjects();
  }

  componentWillUnmount() {
    this.isMounted = false;
  }

  init() {
    const user = this.view.getCurrentUser();
    this.limit = 50; // Increased limit to fetch more events at once
    this.where = {};
    this.search = {};
    this.filter = {};
    this.include = [];
    this.keys = undefined;
    this.sort = { startDate: 1, startTime: 1 }; // Sort by date/time for better data organization
    this.progress = true;
    this.reset();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async getHrEvents(hr) {
    const collection = "hrCalendar"; // Changed to use hrCalendar collection
    const hrId = hr.id;

    if (!hrId) {
      console.error("No HR ID provided to getHrEvents");
      return [];
    }

    try {
      console.log("Fetching events from collection:", collection);
      console.log("Using HR ID for filtering:", hrId);

      // Create a direct query to filter events for the specific HR
      const query = {
        where: {
          "hr.id": hrId, // Changed field name to hr.id to match the collection schema
        },
        // No limit to get all events for the HR
        sort: { startDate: 1, startTime: 1 }, // Sort by date and time
      };

      console.log("Query for events:", JSON.stringify(query));
      const events = await this.findObjectUseCase.execute(collection, query);
      console.log("Events retrieved:", events);

      // Process the events to ensure they have the right format
      if (!events) {
        console.warn("No events found or undefined response");
        return [];
      }

      if (!Array.isArray(events)) {
        console.warn("Events is not an array, converting to array format");
        return typeof events === "object" ? [events] : [];
      }

      // Ensure all events have proper time formats
      return events.map((event) => {
        // Convert time formats if needed (ensuring 24-hour format)
        if (event.startTime && !event.startTime.includes(":")) {
          const hour = parseInt(event.startTime, 10);
          event.startTime = `${hour.toString().padStart(2, "0")}:00`;
        }

        if (event.endTime && !event.endTime.includes(":")) {
          const hour = parseInt(event.endTime, 10);
          event.endTime = `${hour.toString().padStart(2, "0")}:00`;
        }

        return event;
      });
    } catch (error) {
      console.error("Error fetching HR events:", error);
      return [];
    }
  }

  async findObjects() {
    if (!this.isMounted) return;

    const collection = this.view.getCollectionName();
    const query = this.createQuery();

    try {
      this.showProgress();

      if (this.findObjectUseCase?.abort) {
        this.findObjectUseCase.abort();
      }

      const objects = await this.findObjectUseCase.execute(collection, query);

      if (!this.isMounted) return;

      this.objects = this.objects.concat(objects);
      this.view.setTotal(this.objects.length);
      this.view.setObjects(this.objects);
      this.hideProgress();
    } catch (error) {
      if (!this.isMounted) return;

      this.hideProgress();

      if (error.name !== "AbortError") {
        this.view.showError(error);
      }
    }
    this.progress = false;
  }

  async getLocations() {
    if (!this.isMounted) return;

    const collection = "locations";
    try {
      if (this.findObjectUseCase?.abort) {
        this.findObjectUseCase.abort();
      }

      const objects = await this.findObjectUseCase.execute(collection);

      if (!this.isMounted) return;

      this.view.setState({ locations: objects });
    } catch (error) {
      if (!this.isMounted) return;

      console.error("Error fetching locations:", error);

      if (error.name !== "AbortError") {
        this.view.showError(error);
      }
    } finally {
      this.progress = false;
    }
  }

  onClickAdd() {
    this.view.navigateTo("/admin/employee/form");
  }

  async onClickView(empDetails) {
    this.view.navigateTo(`/me/hr/calendar/`);
  }

  onChangeLocation(value, schema) {
    this.reset();
    this.filter = {
      location: {
        name: {
          $regex: value,
          $options: "i",
        },
      },
    };
    this.getObjects();
  }

  onChangeReference(value, schema) {
    this.reset();
    this.filter = {
      referenceNumber: { $regex: value, $options: "i" },
    };
    this.getObjects();
  }
}

export default MyCalendarPresenter;
